import React, { useState, useRef, useCallback, useEffect } from 'react';
import './App.css';
import Sidebar from './components/Sidebar';
import MapView from './components/MapView';
import Toast from './components/Toast';
import IconPickerModal from './components/IconPickerModal';
import { usePOIs } from './hooks/usePOIs';
import { useToast } from './hooks/useToast';

async function geocode(q) {
  const res = await fetch(
    `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=1`,
    { headers: { 'Accept-Language': 'en' } }
  );
  const d = await res.json();
  if (!d.length) throw new Error('Not found');
  return { lat: +d[0].lat, lng: +d[0].lon };
}

export default function App() {
  const [isDark, setIsDark] = useState(() => {
    try { return localStorage.getItem('poi-theme') === 'dark'; } catch { return false; }
  });
  const [useMetric, setUseMetric] = useState(() => {
    try { return localStorage.getItem('poi-unit') === 'km'; } catch { return false; }
  });
  const [mainLatLng, setMainLatLng] = useState(null);
  const [addressText, setAddressText] = useState('');
  const [radius, setRadius] = useState(1609);
  const [focusPOIId, setFocusPOIId] = useState(null);
  const [iconModal, setIconModal] = useState({ open: false, poiId: null });
  const mapRef = useRef(null);
  const { toast, showToast } = useToast();
  const { pois, addPOI, removePOI, updatePOIName, updatePOIIcon, updatePOIPosition, recalcDistances } = usePOIs(mainLatLng);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
    try { localStorage.setItem('poi-theme', isDark ? 'dark' : 'light'); } catch {}
  }, [isDark]);

  useEffect(() => {
    try { localStorage.setItem('poi-unit', useMetric ? 'km' : 'mi'); } catch {}
  }, [useMetric]);

  const handleSearch = useCallback(async (address) => {
    try {
      const { lat, lng } = await geocode(address);
      const newMain = { lat, lng };
      setMainLatLng(newMain);
      setAddressText(address);
      recalcDistances(newMain);
      showToast('Location set');
    } catch {
      showToast('Address not found', true);
    }
  }, [recalcDistances, showToast]);

  const handleFetchSuggestion = useCallback(async (s) => {
    if (!mainLatLng) return showToast('Set a location first', true);
    showToast(`Searching ${s.label}…`);
    let f = s.amenity ? `["amenity"="${s.amenity}"]` : s.leisure ? `["leisure"="${s.leisure}"]` : `["shop"]`;
    const q = `(node${f}(around:${radius},${mainLatLng.lat},${mainLatLng.lng});way${f}(around:${radius},${mainLatLng.lat},${mainLatLng.lng}););out center 6;`;
    try {
      const res = await fetch(`https://overpass-api.de/api/interpreter?data=[out:json];${q}`);
      const data = await res.json();
      const els = (data.elements || []).filter(e => (e.lat ?? e.center?.lat) && (e.lon ?? e.center?.lon));
      if (!els.length) return showToast(`No ${s.label} found`, true);
      els.slice(0, 5).forEach(e => addPOI({
        lat: e.lat ?? e.center.lat,
        lng: e.lon ?? e.center.lon,
        name: e.tags?.name || s.label,
        iconKey: s.key,
      }));
      showToast(`Added ${Math.min(els.length, 5)} ${s.label}`);
    } catch {
      showToast('Could not fetch — try again', true);
    }
  }, [mainLatLng, radius, addPOI, showToast]);

  const handleAddManual = useCallback(() => {
    if (!mainLatLng) return showToast('Set a location first', true);
    addPOI({ lat: mainLatLng.lat + 0.0012, lng: mainLatLng.lng + 0.0012, name: 'New Point', iconKey: 'marker' });
    showToast('Drag the marker to reposition');
  }, [mainLatLng, addPOI, showToast]);

  const handleExport = useCallback(async () => {
    const m = mapRef.current;
    if (!m) return showToast('Search for a location first', true);
    showToast('Building export…');

    const mapEl = document.querySelector('.leaflet-container');
    if (!mapEl) return showToast('Map not ready', true);

    const mapW = mapEl.offsetWidth || 800;
    const mapH = mapEl.offsetHeight || 600;
    const tileImgs = Array.from(mapEl.querySelectorAll('.leaflet-tile-pane img.leaflet-tile'))
      .filter(img => img.complete && img.naturalWidth > 0);
    const mapRect = mapEl.getBoundingClientRect();

    const LEGEND_W = 300, PAD = 20, HDR_H = 60, ROW_H = 44;
    const legendH = HDR_H + PAD + 38 + pois.length * ROW_H + PAD * 2;
    const canvasH = Math.max(mapH, legendH);

    const canvas = document.createElement('canvas');
    canvas.width = mapW + LEGEND_W;
    canvas.height = canvasH;
    const ctx = canvas.getContext('2d');

    ctx.fillStyle = '#aad3df';
    ctx.fillRect(0, 0, mapW, canvasH);

    ctx.save();
    ctx.beginPath(); ctx.rect(0, 0, mapW, mapH); ctx.clip();
    for (const img of tileImgs) {
      const r = img.getBoundingClientRect();
      try { ctx.drawImage(img, r.left - mapRect.left, r.top - mapRect.top, r.width || 256, r.height || 256); } catch (_) {}
    }
    ctx.restore();

    if (mapH < canvasH) { ctx.fillStyle = '#f5f6fa'; ctx.fillRect(0, mapH, mapW, canvasH - mapH); }

    const center = m.getCenter(), zoom = m.getZoom();
    function px(lat, lng) {
      const sc = Math.pow(2, zoom) * 256;
      const lx = l => (l + 180) / 360 * sc;
      const ly = l => (0.5 - Math.log((1 + Math.sin(l * Math.PI / 180)) / (1 - Math.sin(l * Math.PI / 180))) / (4 * Math.PI)) * sc;
      return { x: mapW / 2 + lx(lng) - lx(center.lng), y: mapH / 2 + ly(lat) - ly(center.lat) };
    }

    if (mainLatLng) {
      const { x, y } = px(mainLatLng.lat, mainLatLng.lng);
      ctx.shadowColor = 'rgba(99,102,241,.4)'; ctx.shadowBlur = 10;
      ctx.beginPath(); ctx.arc(x, y, 11, 0, Math.PI * 2);
      ctx.fillStyle = '#6366f1'; ctx.fill();
      ctx.shadowBlur = 0; ctx.strokeStyle = '#fff'; ctx.lineWidth = 3; ctx.stroke();
    }

    pois.forEach((poi, i) => {
      const { x, y } = px(poi.lat, poi.lng);
      ctx.shadowColor = 'rgba(0,0,0,.3)'; ctx.shadowBlur = 8;
      ctx.beginPath(); ctx.arc(x, y, 12, 0, Math.PI * 2);
      ctx.fillStyle = '#0d1117'; ctx.fill();
      ctx.shadowBlur = 0;
      ctx.fillStyle = '#fff'; ctx.font = 'bold 9px Inter,sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(i + 1, x, y);
    });
    ctx.shadowBlur = 0;

    const lx = mapW;
    ctx.fillStyle = '#ffffff'; ctx.fillRect(lx, 0, LEGEND_W, canvasH);
    ctx.fillStyle = '#e9edf2'; ctx.fillRect(lx, 0, 1, canvasH);

    ctx.fillStyle = '#6366f1';
    ctx.beginPath();
    if (ctx.roundRect) ctx.roundRect(lx + PAD, PAD, LEGEND_W - PAD * 2, HDR_H - PAD, 10);
    else ctx.rect(lx + PAD, PAD, LEGEND_W - PAD * 2, HDR_H - PAD);
    ctx.fill();

    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 14px Inter,sans-serif';
    ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
    ctx.fillText('Points of Interest', lx + PAD + 14, PAD + (HDR_H - PAD) / 2 - 6);
    ctx.font = '11px Inter,sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,.75)';
    const addr = addressText;
    ctx.fillText(addr.length > 34 ? addr.slice(0, 34) + '…' : addr, lx + PAD + 14, PAD + (HDR_H - PAD) / 2 + 9);

    let rowY = HDR_H + PAD;
    ctx.fillStyle = '#e9edf2'; ctx.fillRect(lx + PAD, rowY, LEGEND_W - PAD * 2, 1);
    rowY += 12;

    ctx.beginPath(); ctx.arc(lx + PAD + 10, rowY + 10, 10, 0, Math.PI * 2);
    ctx.fillStyle = '#6366f1'; ctx.fill();
    ctx.strokeStyle = '#fff'; ctx.lineWidth = 2; ctx.stroke();
    ctx.fillStyle = '#0d1117'; ctx.font = '600 12px Inter,sans-serif';
    ctx.textAlign = 'left'; ctx.textBaseline = 'top';
    ctx.fillText('Main Location', lx + PAD + 26, rowY + 2);
    ctx.fillStyle = '#9ca3af'; ctx.font = '10px Inter,sans-serif';
    ctx.fillText(addr.length > 32 ? addr.slice(0, 32) + '…' : addr, lx + PAD + 26, rowY + 16);
    rowY += 40;

    pois.forEach((poi, i) => {
      const ry = rowY + i * ROW_H;
      ctx.fillStyle = i % 2 === 0 ? '#f9fafb' : '#ffffff';
      ctx.fillRect(lx + 1, ry, LEGEND_W - 1, ROW_H);
      ctx.fillStyle = '#0d1117';
      ctx.beginPath();
      if (ctx.roundRect) ctx.roundRect(lx + PAD, ry + ROW_H / 2 - 10, 20, 20, 4);
      else ctx.rect(lx + PAD, ry + ROW_H / 2 - 10, 20, 20);
      ctx.fill();
      ctx.fillStyle = '#fff'; ctx.font = 'bold 9px Inter,sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(i + 1, lx + PAD + 10, ry + ROW_H / 2);
      ctx.textAlign = 'left';
      ctx.fillStyle = '#0d1117'; ctx.font = '600 11px Inter,sans-serif';
      ctx.textBaseline = 'top';
      const nm = poi.name.length > 28 ? poi.name.slice(0, 28) + '…' : poi.name;
      ctx.fillText(nm, lx + PAD + 26, ry + 9);
      const dist = poi.distance !== undefined
        ? (useMetric ? (poi.distance < 1 ? `${Math.round(poi.distance * 1000)} m` : `${poi.distance.toFixed(2)} km`) : `${(poi.distance * 0.621371).toFixed(2)} mi`)
        : '';
      ctx.fillStyle = '#9ca3af'; ctx.font = '10px Inter,sans-serif';
      ctx.fillText(`${poi.iconKey}${dist ? ' · ' + dist : ''}`, lx + PAD + 26, ry + 23);
    });

    ctx.fillStyle = '#d1d9e0'; ctx.font = '9px Inter,sans-serif';
    ctx.textAlign = 'right'; ctx.textBaseline = 'bottom';
    ctx.fillText('© OpenStreetMap contributors', lx + LEGEND_W - PAD, canvasH - 6);

    const link = document.createElement('a');
    link.download = 'points-of-interest.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
    showToast('Downloaded ✓');
  }, [mainLatLng, pois, addressText, useMetric, showToast]);

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <Sidebar
        isDark={isDark} toggleTheme={() => setIsDark(d => !d)}
        mainLatLng={mainLatLng} onSearch={handleSearch}
        radius={radius} onRadiusChange={setRadius}
        useMetric={useMetric} onToggleUnit={() => setUseMetric(m => !m)}
        pois={pois}
        onAddManualPOI={handleAddManual}
        onRemovePOI={removePOI}
        onUpdateName={updatePOIName}
        onIconClick={(id) => setIconModal({ open: true, poiId: id })}
        onFocusPOI={setFocusPOIId}
        onFetchSuggestion={handleFetchSuggestion}
        onExport={handleExport}
      />
      <MapView
        mainLatLng={mainLatLng}
        radius={radius}
        pois={pois}
        onPOIDragEnd={updatePOIPosition}
        focusPOIId={focusPOIId}
        setFocusPOIId={setFocusPOIId}
        mapRef={mapRef}
      />
      <IconPickerModal
        open={iconModal.open}
        currentIconKey={pois.find(p => p.id === iconModal.poiId)?.iconKey}
        onSelect={(key) => {
          updatePOIIcon(iconModal.poiId, key);
          setIconModal({ open: false, poiId: null });
        }}
        onClose={() => setIconModal({ open: false, poiId: null })}
      />
      <Toast {...toast} />
    </div>
  );
}
